import React, { useEffect, useState } from "react";
import ModalBasicLayout from "../ModalBasicLayout";
import {
  MODAL_VERIFY_INTEGRATION_SCOPES_DESCRIPTION,
  MODAL_VERIFY_INTEGRATION_SCOPES_TITLE,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import { useSelectorApp } from "../../../lib/redux";
import _ from "lodash";
import { TIKTOK, TIKTOK_BUSINESS_SCOPES } from "../../../utils/constants";
import {
  getPlatformFromIntegrationKey,
  isAdsPlatformIntegrationKey,
} from "../../../actions/getters";
import { getMissingIntegrationScopes } from "../../../utils/array";
import {
  isVerifyScopesDismissedInSession,
  markVerifyScopesDismissedInSession,
} from "../../../utils/verifyIntegrationScopesDismissal";
import VerifyIntegrationScopesItem from "./VerifyIntegrationScopesItem";
import Box from "@mui/material/Box";

/** Logical ads platforms; RTDB keys via getAdsIntegrationKey (Meta → facebook, TikTok → tiktokBusiness). */
const PLATFORMS_TO_WATCH = [TIKTOK];
const SCOPES = {
  [TIKTOK]: TIKTOK_BUSINESS_SCOPES,
};

const ModalVerifyIntegrationScopes = React.memo(
  function ModalVerifyIntegrationScopes() {
    const [modalOpen, setModalOpen] = useState(false);
    const [integrationsToReconnect, setIntegrationsToReconnect] = useState([]);

    const { t } = useTranslationApp();
    const uid = useSelectorApp((state) => state?.auth?.user?.uid);
    const integrations = useSelectorApp((state) => state?.user?.integrations);
    const integrationsKeys = Object.keys(integrations || {});

    const closeModal = () => setModalOpen(false);

    const onCloseModal = () => {
      // Remember the dismissal so it survives navigation until reload.
      markVerifyScopesDismissedInSession({ uid });
      closeModal();
    };

    const getName = ({ data }) => data.name || data.id;

    const getIntegrationsToReconnect = () => {
      const integrationsToReconnect = integrationsKeys.filter((key) =>
        isAdsPlatformIntegrationKey(key, PLATFORMS_TO_WATCH)
      );

      const integrationsToReconnectFiltered = [];

      integrationsToReconnect.forEach((integrationKey) => {
        const integrationsByPlatform = integrations[integrationKey];
        if (
          !integrationsByPlatform ||
          typeof integrationsByPlatform !== "object"
        ) {
          return;
        }

        for (const integrationID in integrationsByPlatform) {
          const integration = integrationsByPlatform[integrationID];
          const platform = getPlatformFromIntegrationKey(integrationKey);

          const missingPermissions = getMissingIntegrationScopes({
            currentArray: integration.scopes,
            targetArray: SCOPES[platform],
          });
          if (_.isEmpty(missingPermissions)) continue;

          integrationsToReconnectFiltered.push({
            platform,
            integrationID,
            name: getName({ data: integration }),
            permissions: missingPermissions,
          });
        }
      });

      // Already dismissed in this session: keep the modal closed until reload.
      if (isVerifyScopesDismissedInSession({ uid })) {
        setIntegrationsToReconnect(integrationsToReconnectFiltered);
        return;
      }

      setIntegrationsToReconnect(integrationsToReconnectFiltered);
      if (integrationsToReconnectFiltered.length > 0) setModalOpen(true);
    };

    useEffect(() => {
      if (_.isEmpty(integrations)) return;

      const hasIntegrationsToWatch = integrationsKeys.some((key) =>
        isAdsPlatformIntegrationKey(key, PLATFORMS_TO_WATCH)
      );
      if (!hasIntegrationsToWatch) return;

      getIntegrationsToReconnect();
      // eslint-disable-next-line
    }, [integrations]);

    useEffect(() => {
      if (integrationsToReconnect.length === 0 && modalOpen) closeModal();

      // eslint-disable-next-line
    }, [integrationsToReconnect]);

    return (
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={async () => {
          onCloseModal();
        }}
        maxWidth="md"
        scroll="paper"
        closeButtonSx={{ fontSize: "1.05rem", p: 0.35 }}
        title={t(MODAL_VERIFY_INTEGRATION_SCOPES_TITLE)}
        description={t(MODAL_VERIFY_INTEGRATION_SCOPES_DESCRIPTION)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {integrationsToReconnect.map((integration) => (
            <VerifyIntegrationScopesItem
              key={`${integration.platform}:${integration.integrationID}`}
              data={integration}
            />
          ))}
        </Box>
      </ModalBasicLayout>
    );
  }
);

export default ModalVerifyIntegrationScopes;
